import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Gps, Locale, SubmitInspectionInput } from '@prooffleet/contracts';

/**
 * Локальное хранилище водительского приложения.
 *
 * Дворы и склады — мёртвая зона, поэтому осмотр целиком собирается офлайн:
 * черновик и снимки живут здесь, пока сервер не подтвердит приём.
 * Клиент НЕ чистит локальную копию до подтверждения — иначе однажды
 * потеряется именно то фото, которое понадобится.
 */

export interface ItemDraft {
  itemId: string;
  skipped: boolean;
  boolValue: boolean | null;
  measurementValue: number | null;
  note: string | null;
  defectReported: boolean;
  /** Локальные идентификаторы снимков в хранилище blobs. */
  photoIds: string[];
  /**
   * Локальный идентификатор голосовой заметки.
   *
   * Заметка одна на пункт: диктуют описание дефекта, а не серию реплик.
   * Хранится тем же путём, что снимки, и уходит той же очередью.
   */
  audioId: string | null;
  /** Правило 6: своё время у каждого пункта, разрывы должны быть видны. */
  capturedAt: string;
  gps: Gps | null;
}

export interface InspectionDraft {
  draftId: string;
  unitId: string;
  unitNumber: string;
  templateKey: string;
  templateVersion: number;
  /** Язык, на котором водитель видел чек-лист (правило 2). */
  locale: Locale;
  startedAt: string;
  items: Record<string, ItemDraft>;
  gps: Gps | null;
  updatedAt: string;
}

export interface StoredBlob {
  localId: string;
  draftId: string;
  bytes: ArrayBuffer;
  sha256: string;
  mime: string;
  extension: string;
  /** Заполняется после успешной загрузки в хранилище. */
  objectKey: string | null;
  versionId: string | null;
}

export interface QueuedSubmission {
  clientUuid: string;
  draftId: string;
  unitNumber: string;
  /** Снимки в payload ссылаются на localId; ключи объектов подставляются при отправке. */
  payload: Omit<SubmitInspectionInput, 'items'> & {
    items: (Omit<SubmitInspectionInput['items'][number], 'photos' | 'audio'> & {
      photoLocalIds: string[];
      audioLocalId: string | null;
    })[];
  };
  attempts: number;
  lastError: string | null;
  createdAt: string;
}

interface ProofFleetDB extends DBSchema {
  drafts: { key: string; value: InspectionDraft };
  blobs: { key: string; value: StoredBlob; indexes: { byDraft: string } };
  queue: { key: string; value: QueuedSubmission };
}

let dbPromise: Promise<IDBPDatabase<ProofFleetDB>> | null = null;

export function db(): Promise<IDBPDatabase<ProofFleetDB>> {
  dbPromise ??= openDB<ProofFleetDB>('prooffleet', 1, {
    upgrade(database) {
      database.createObjectStore('drafts', { keyPath: 'draftId' });
      const blobs = database.createObjectStore('blobs', { keyPath: 'localId' });
      blobs.createIndex('byDraft', 'draftId');
      database.createObjectStore('queue', { keyPath: 'clientUuid' });
    },
  });
  return dbPromise;
}

/* --------------------------------- черновики ------------------------------ */

export async function saveDraft(draft: InspectionDraft): Promise<void> {
  const conn = await db();
  await conn.put('drafts', { ...draft, updatedAt: new Date().toISOString() });
}

export async function loadDraft(draftId: string): Promise<InspectionDraft | undefined> {
  return (await db()).get('drafts', draftId);
}

export async function activeDrafts(): Promise<InspectionDraft[]> {
  return (await db()).getAll('drafts');
}

export async function deleteDraft(draftId: string): Promise<void> {
  const conn = await db();
  const tx = conn.transaction(['drafts', 'blobs'], 'readwrite');
  await tx.objectStore('drafts').delete(draftId);

  const index = tx.objectStore('blobs').index('byDraft');
  for (const blob of await index.getAll(draftId)) {
    await tx.objectStore('blobs').delete(blob.localId);
  }
  await tx.done;
}

/* ---------------------------------- снимки -------------------------------- */

export async function putBlob(blob: StoredBlob): Promise<void> {
  await (await db()).put('blobs', blob);
}

export async function getBlob(localId: string): Promise<StoredBlob | undefined> {
  return (await db()).get('blobs', localId);
}

export async function markBlobUploaded(
  localId: string,
  objectKey: string,
  versionId: string | null,
): Promise<void> {
  const conn = await db();
  const existing = await conn.get('blobs', localId);
  if (existing === undefined) return;
  await conn.put('blobs', { ...existing, objectKey, versionId });
}

/* --------------------------------- очередь -------------------------------- */

export async function enqueue(submission: QueuedSubmission): Promise<void> {
  await (await db()).put('queue', submission);
}

export async function queued(): Promise<QueuedSubmission[]> {
  return (await db()).getAll('queue');
}

export async function updateQueued(submission: QueuedSubmission): Promise<void> {
  await (await db()).put('queue', submission);
}

export async function dequeue(clientUuid: string): Promise<void> {
  await (await db()).delete('queue', clientUuid);
}

/**
 * Оценка занятого места. На iOS квота скромная, поэтому подтверждённые
 * осмотры чистятся агрессивно — но только после подтверждения сервером.
 */
export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (navigator.storage?.estimate === undefined) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usage, quota };
}
